import type {
  Account,
  AppRole,
  BankAccount,
  ClientGroup,
  AfrDocumentKindRow,
  Company,
  CompanyAfrFiling,
  CompanyAfrFilingDocument,
  Contact,
  ContactRole,
  Country,
  FeeSchedule,
  FeeSchedulePrice,
  Filter,
  FilterOperator,
  Invoice,
  InvoiceExportLine,
  InvoiceLine,
  InvoiceNote,
  ListQuery,
  NotificationFeed,
  OrderBy,
  PageMeta,
  PriceBook,
  RemittanceDefaults,
  InvoiceSettings,
  BulkAction,
  Service,
  ServiceBundle,
  ServiceBundleItemWithService,
  ServiceCategory,
  ServiceEvent,
  SundryExpense,
  VirrginImportResult,
  VirrginImportRow,
  VirrginRematchResult,
  VirrginSummary,
  VirrginTransaction,
} from "@somasi/shared";
import {
  BANK_ACCOUNTS_PATH,
  CLIENT_GROUPS_PATH,
  AFR_DOCUMENTS_PATH,
  AFR_DOCUMENT_KINDS_PATH,
  AFR_FILINGS_PATH,
  afrDocumentType,
  COMPANIES_PATH,
  companyAfrDocumentDownloadPath,
  companyAfrDocumentUploadUrlPath,
  companyAfrDocumentsPath,
  companyAfrFilePath,
  companyAfrFilingPath,
  companyAfrFilingsPath,
  CONTACTS_PATH,
  COUNTRIES_PATH,
  FEE_SCHEDULES_PATH,
  INVOICE_NOTES_PATH,
  INVOICES_PATH,
  MAX_PAGE_SIZE,
  REMITTANCE_DEFAULTS_PATH,
  INVOICE_SETTINGS_PATH,
  BULK_ACTIONS_PATH,
  SERVICE_BUNDLES_PATH,
  SERVICE_CATEGORIES_PATH,
  SERVICES_PATH,
  serviceBundleItemsPath,
  VIRRGIN_TRANSACTIONS_PATH,
  WORK_LOG_EVENTS_PATH,
  WORK_LOG_EXPENSES_PATH,
  encodeListQuery,
  feeScheduleClientGroupsPath,
  feeScheduleLinesPath,
  feeSchedulePriceBookPath,
  feeScheduleStandardPath,
  feeScheduleVersionPath,
  feeScheduleVersionsPath,
  invoiceNotesReorderPath,
  serviceSchedulesPath,
  virrginImportPath,
  virrginRematchPath,
  virrginSummaryPath,
} from "@somasi/shared";
import type {
  AmendmentAllowance,
  AttendanceDay,
  AttendanceStatus,
  DayEffort,
  EffortTotals,
  TimeAmendment,
  TimeAmendmentStatus,
  TimeEntry,
  WorkDayWindow,
  DayHalf,
  Employee,
  EmployeeContract,
  EmployeeDocument,
  EmployeeDocumentKind,
  FilingDocumentKind,
  EmployeeLeaveBalances,
  EmployeePayItem,
  Holiday,
  LeaveAllowance,
  LeaveRequest,
  LeaveRequestStatus,
  LeaveType,
  OfficeNetwork,
  PayPeriod,
  PayPeriodStatus,
  PayrollFiling,
  PayrollFilingDocument,
  PayrollRate,
  Person,
  Payslip,
  PayslipBreakdown,
  WfhRequest,
} from "@somasi/shared";
import {
  ATTENDANCE_PATH,
  EMPLOYEES_PATH,
  meNotificationsPath,
  HOLIDAYS_PATH,
  LEAVE_ALLOWANCES_PATH,
  leaveAllowancesSetPath,
  LEAVE_REQUESTS_PATH,
  LEAVE_TYPES_PATH,
  OFFICE_NETWORKS_PATH,
  PAYROLL_FILINGS_PATH,
  PAYROLL_RATES_PATH,
  PAYSLIPS_PATH,
  PAY_PERIODS_PATH,
  TIME_AMENDMENTS_PATH,
  WFH_REQUESTS_PATH,
  wfhRequestDecisionPath,
  attendanceDayPath,
  attendanceEntryDecisionPath,
  attendancePeoplePath,
  attendanceTimesheetPath,
  timeAmendmentAllowancePath,
  timeAmendmentDecisionPath,
  employeeDocumentDownloadPath,
  employeeDocumentUploadUrlPath,
  employeeDocumentsPath,
  payrollFilingDocumentDownloadPath,
  payrollFilingDocumentUploadUrlPath,
  payrollFilingDocumentsPath,
  employeeLeaveBalancesPath,
  employeeAccountPath,
  employeeAdoptAccountPath,
  employeeContractPath,
  employeeRolesPath,
  employeePayItemsPath,
  payPeriodActionPath,
  payPeriodPayslipsPath,
} from "@somasi/shared";
import type { PayslipPdfData } from "@somasi/shared/payslip-pdf";
import { apiFetch, ApiError } from "./http";

/**
 * Typed clients for the entity REST resources.
 *
 * The generic `db.from(table)` client put the table name in the request body, so
 * every page took the same route and the same permission regardless of what it
 * was reading. These call one resource each —
 *
 *   companies.list({ order: … })   GET  /companies?order=legal_name.asc
 *   companies.get(id)              GET  /companies/{id}
 *   companies.create(values)       POST /companies
 *   companies.update(id, patch)    PATCH /companies/{id}
 *   companies.remove(id)           DELETE /companies/{id}
 *
 * — and the row type comes from the resource, not from a table-name generic, so
 * a misspelled column is a compile error rather than a runtime `null`.
 */

export interface Result<T> {
  data: T;
  error: { message: string; code?: string } | null;
  count?: number | null;
  /** Where the returned rows sit in the matching set; list calls only. */
  page?: PageMeta | null;
}

function toError(error: unknown): { message: string; code?: string } {
  if (error instanceof ApiError) return { message: error.message, code: error.code };
  if (error instanceof Error) return { message: error.message };
  return { message: "Unexpected error" };
}

async function call<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<Result<T | null>> {
  try {
    const response = await apiFetch<T>(path, init);
    return {
      data: response.data,
      error: null,
      count: response.count ?? null,
      page: response.page ?? null,
    };
  } catch (error) {
    return { data: null, error: toError(error), count: null, page: null };
  }
}

/** Chainable list query, so the shape at the call sites barely changes. */
export class ListBuilder<T> implements PromiseLike<Result<T[] | null>> {
  private readonly query: ListQuery = {};

  constructor(
    private readonly basePath: string,
    private readonly extra: Record<string, string> = {},
  ) {}

  private where(column: string, op: FilterOperator, value: unknown, negate = false): this {
    (this.query.filters ??= []).push({ column, op, value, negate });
    return this;
  }

  select(...columns: string[]) {
    this.query.select = columns.flatMap((c) => c.split(",").map((p) => p.trim()));
    return this;
  }
  not(column: string, op: FilterOperator, value: unknown) {
    return this.where(column, op, value, true);
  }
  eq(column: string, value: unknown) {
    return this.where(column, "eq", value);
  }
  neq(column: string, value: unknown) {
    return this.where(column, "neq", value);
  }
  gt(column: string, value: unknown) {
    return this.where(column, "gt", value);
  }
  gte(column: string, value: unknown) {
    return this.where(column, "gte", value);
  }
  lt(column: string, value: unknown) {
    return this.where(column, "lt", value);
  }
  lte(column: string, value: unknown) {
    return this.where(column, "lte", value);
  }
  like(column: string, pattern: string) {
    return this.where(column, "like", pattern);
  }
  ilike(column: string, pattern: string) {
    return this.where(column, "ilike", pattern);
  }
  in(column: string, values: readonly unknown[]) {
    return this.where(column, "in", [...values]);
  }
  is(column: string, value: null | boolean) {
    return this.where(column, "is", value);
  }
  /**
   * Membership of a list column — "this note names this company".
   *
   * `eq` cannot ask it: the stored value is the whole array, so comparing it to
   * one id is false however many ids it holds.
   */
  contains(column: string, value: unknown) {
    return this.where(column, "contains", value);
  }

  /**
   * OR group: a row matches when any one clause does.
   *
   * This is what a search box needs. `eq`/`ilike` and friends accumulate as a
   * conjunction, so "the text appears in the code or the name" cannot be said
   * with them — every extra clause would narrow the result instead of widening
   * it. The group as a whole is still ANDed with the other filters, so a search
   * inside a status filter stays inside it.
   */
  or(clauses: { column: string; op: FilterOperator; value: unknown }[]) {
    if (clauses.length) this.query.or = clauses.map((c) => ({ ...c }));
    return this;
  }

  order(column: string, options?: { ascending?: boolean }) {
    (this.query.order ??= []).push({ column, ascending: options?.ascending !== false });
    return this;
  }
  limit(count: number) {
    this.query.limit = count;
    return this;
  }
  range(from: number, to: number) {
    this.query.offset = from;
    this.query.limit = to - from + 1;
    return this;
  }

  /**
   * One page of the list, 1-based, with the total alongside it.
   *
   * What a table footer needs, in the spelling it thinks in. `range` is still
   * there for the callers that have an offset rather than a page number.
   */
  page(page: number, pageSize: number) {
    this.query.offset = Math.max(0, (page - 1) * pageSize);
    this.query.limit = pageSize;
    this.query.count = true;
    return this;
  }

  /**
   * Every matching row, up to the API's ceiling.
   *
   * The list endpoints answer with one page when no `limit` is sent, which is
   * right for a table and wrong for the reads that are not tables: the option
   * lists behind the dropdowns, the lookup maps a page joins against, the rows
   * an export writes out. Those say `.all()`, so the unbounded read is a word at
   * the call site rather than an omission — and `MAX_PAGE_SIZE` keeps it from
   * being truly unbounded, since the server refuses anything larger.
   */
  all() {
    this.query.limit = MAX_PAGE_SIZE;
    return this;
  }

  /**
   * Every matching row, paging through the API until the set is complete.
   *
   * `all()` asks for one page of `MAX_PAGE_SIZE`, which is the right read for an
   * option list — a dropdown of more than a thousand rows is not a dropdown —
   * and the wrong one for an export of a table that only grows. Past a thousand
   * rows that file is quietly short, and a spreadsheet does not say where it
   * stopped; `page.total` does, so this walks the windows until it has them all.
   *
   * `max` is the point at which it stops walking, and `truncated` says it did,
   * so a caller can tell the operator rather than hand over a file that claims
   * to be everything. Unlike `all()` this is a method and not a chain step: it
   * runs the requests itself, because it is several of them.
   */
  async collect(
    options: { max?: number } = {},
  ): Promise<Result<T[] | null> & { truncated: boolean }> {
    const max = options.max ?? MAX_PAGE_SIZE * 20;
    const rows: T[] = [];

    for (;;) {
      this.query.offset = rows.length;
      this.query.limit = Math.min(MAX_PAGE_SIZE, max - rows.length);
      this.query.count = true;

      const result = await this.run();
      if (result.error) return { ...result, truncated: false };

      const page = result.data ?? [];
      rows.push(...page);

      const total = result.page?.total ?? result.count ?? rows.length;
      // A short page means the server had no more to give, whatever the total
      // said — a row deleted between two of these requests would otherwise
      // leave this asking for a window past the end for ever.
      if (page.length === 0 || rows.length >= total) {
        return { data: rows, error: null, count: rows.length, truncated: false };
      }
      if (rows.length >= max) {
        return { data: rows, error: null, count: rows.length, truncated: true };
      }
    }
  }
  /** Ask for the exact total alongside the page. */
  withCount() {
    this.query.count = true;
    return this;
  }
  /** The total only — no rows come back. */
  countOnly(): PromiseLike<Result<null>> {
    this.query.count = true;
    this.query.head = true;
    return this as unknown as PromiseLike<Result<null>>;
  }

  private async run(): Promise<Result<T[] | null>> {
    const params = new URLSearchParams(encodeListQuery(this.query));
    for (const [key, value] of Object.entries(this.extra)) params.set(key, value);
    const search = params.toString();
    return call<T[]>(search ? `${this.basePath}?${search}` : this.basePath);
  }

  then<R1 = Result<T[] | null>, R2 = never>(
    onfulfilled?: ((value: Result<T[] | null>) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: unknown) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    return this.run().then(onfulfilled, onrejected);
  }
}

/** The five standard operations of one resource. */
class Resource<Row, Draft = Partial<Row>> {
  constructor(private readonly basePath: string) {}

  list(extra: Record<string, string> = {}) {
    return new ListBuilder<Row>(this.basePath, extra);
  }

  get(id: string, columns?: string[]) {
    const search = columns?.length ? `?select=${columns.join(",")}` : "";
    return call<Row>(`${this.basePath}/${encodeURIComponent(id)}${search}`);
  }

  create(values: Draft) {
    return call<Row>(this.basePath, { method: "POST", body: values });
  }

  /**
   * Several rows in one request, refused as a whole if any of them is bad.
   *
   * The alternative — a `create` per row from here — would make the browser the
   * thing orchestrating a multi-row write, so a row rejected halfway would leave
   * the earlier ones saved and the operator guessing which. One call keeps the
   * validation on the server, where the rest of it already is.
   */
  createMany(values: Draft[]) {
    return call<Row[]>(this.basePath, { method: "POST", body: values });
  }

  update(id: string, patch: Draft) {
    return call<Row>(`${this.basePath}/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: patch,
    });
  }

  remove(id: string) {
    return call<Row>(`${this.basePath}/${encodeURIComponent(id)}`, { method: "DELETE" });
  }
}

export type CountryDraft = Partial<
  Omit<Country, "id" | "created_at" | "updated_at" | "created_by">
>;
export type CompanyDraft = Partial<
  Omit<Company, "id" | "created_at" | "updated_at" | "created_by">
>;
export type ClientGroupDraft = Partial<
  Omit<ClientGroup, "id" | "created_at" | "updated_at" | "created_by">
>;
export type ContactDraft = Partial<
  Omit<Contact, "id" | "created_at" | "updated_at" | "created_by">
>;

/**
 * A role as the contact dialog edits it; `id` is kept so an unchanged row survives.
 *
 * The place is named by `company_id`, or by `client_group_id` on its own — never
 * both, since a company carries its own group. Neither name is here: the server
 * copies both down from whichever was named, which is the only answer that stays
 * right once one person is attached to companies in different groups.
 */
export type ContactRoleDraft = Partial<
  Omit<
    ContactRole,
    "contact_id" | "created_at" | "updated_at" | "company_name" | "client_group_name"
  >
> & { role: ContactRole["role"] };

/** `Contact` with its roles, as `GET /contacts?include=roles` returns it. */
export type ContactWithRoles = Contact & { roles: ContactRole[] };

export type FeeScheduleDraft = Partial<
  Omit<FeeSchedule, "id" | "created_at" | "updated_at" | "created_by">
>;

/** A price as the editor states it; the schedule and the date come from the path. */
export type FeeSchedulePriceDraft = Partial<
  Omit<FeeSchedulePrice, "fee_schedule_id" | "sk" | "effective_from" | "created_at" | "updated_at">
> & { service_id: string };

/**
 * A version as the pages read it — the stored header plus what the order of the
 * dates implies. Mirrors `VersionSummary` in `lib/fee-schedule-prices.ts`.
 */
export interface FeeScheduleVersionSummary {
  fee_schedule_id: string;
  effective_from: string;
  /** Derived from the next version's start; `null` on the newest. Never stored. */
  effective_to: string | null;
  notes: string | null;
  is_latest: boolean;
  /** Already started, so invoices may have been raised at these prices. */
  is_historical: boolean;
  created_at: string;
  created_by: string | null;
}

/** What dating a new set of prices asks for. */
export interface FeeScheduleVersionDraft {
  effective_from: string;
  notes?: string | null;
  /**
   * The date to copy prices from. Omit for the schedule's newest — "take the
   * current rates and change a few" — or pass `null` to start from nothing.
   */
  copy_from?: string | null;
  /** The prices that differ from the copy. Everything else carries over. */
  prices?: FeeSchedulePriceDraft[];
}

/**
 * The catalogue's own list and the rates it bills on a date, as
 * `GET /fee-schedules/standard` returns it. `null` until a schedule is flagged.
 */
export interface StandardSchedule {
  fee_schedule_id: string;
  name: string;
  active: boolean;
  /** The dated set in force on `as_of`; `null` when the schedule holds none. */
  effective_from: string | null;
  effective_to: string | null;
  as_of: string;
  prices: { service_id: string; fee: number; billing_frequency: string | null }[];
}

class FeeSchedulesResource extends Resource<FeeSchedule, FeeScheduleDraft> {
  /**
   * The standard price list, with the rate it bills for each service.
   *
   * The rates come from the version **in force** on `as_of`, not the newest, so a
   * set that starts next month is not reported as what is charged today. Which
   * version that is stays a server decision, as it does for a company's price list:
   * the browser deciding it again is how the figure on screen and the figure on the
   * invoice came apart before.
   */
  standard(asOf?: string) {
    const search = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
    return call<StandardSchedule | null>(`${feeScheduleStandardPath}${search}`);
  }

  /**
   * Every schedule's rates, in one request — the read behind the catalogue export.
   *
   * `versions: "in_force"` is one version per schedule, the rates billing on
   * `asOf`, and is what an export of current pricing wants. `"all"` is the whole
   * dated history of every schedule, which is bigger by however many versions have
   * been dated and so has to be asked for.
   *
   * Which version counts as in force stays a server decision here as it does for
   * `standard` and for a company's price list: the browser deciding it again is how
   * the figure on screen and the figure on the invoice came apart before.
   *
   * `feeScheduleId` narrows it to one schedule, which is what a single schedule's
   * export wants, with the same resolution the whole book uses. Find that schedule
   * in `data.schedules` by its id rather than reading the first element: this is a
   * filter, and an API that predates it ignores the unknown parameter and answers
   * with every schedule ordered by name — so position silently means "whichever
   * sorts first".
   */
  priceBook(
    options: { asOf?: string; versions?: "in_force" | "all"; feeScheduleId?: string } = {},
  ) {
    const search = new URLSearchParams();
    if (options.asOf) search.set("as_of", options.asOf);
    if (options.versions) search.set("versions", options.versions);
    // One schedule's own export: the same book with one schedule in it, rather
    // than every schedule's history downloaded to read one of them.
    if (options.feeScheduleId) search.set("fee_schedule_id", options.feeScheduleId);
    const query = search.toString();
    return call<PriceBook>(`${feeSchedulePriceBookPath}${query ? `?${query}` : ""}`);
  }

  /**
   * Makes exactly these client groups read this schedule.
   *
   * One request for the whole set. Each group's own `fee_schedule_id` is what moves,
   * server-side, so a group already reading another schedule is moved rather than
   * refused — a group reads one schedule by construction.
   */
  setClientGroups(feeScheduleId: string, clientGroupIds: string[]) {
    return call<{ attached: { id: string; name: string }[]; detachedFrom: string[] }>(
      feeScheduleClientGroupsPath(feeScheduleId),
      { method: "PUT", body: { client_group_ids: clientGroupIds } },
    );
  }

  /** The schedule's dated sets of prices, newest first. */
  versions(feeScheduleId: string) {
    return call<FeeScheduleVersionSummary[]>(feeScheduleVersionsPath(feeScheduleId));
  }

  /**
   * Dates a new set of prices.
   *
   * One request for the whole snapshot. The server copies the set being superseded
   * and applies `prices` over it, so the services nobody changed keep their price
   * rather than arriving absent — which is what made the browser doing this itself
   * unsafe.
   */
  createVersion(feeScheduleId: string, draft: FeeScheduleVersionDraft) {
    return call<{ effective_from: string; copied_from: string | null }>(
      feeScheduleVersionsPath(feeScheduleId),
      { method: "POST", body: draft },
    );
  }

  /**
   * A version's notes. Its date is not editable: the date *is* the version, and
   * moving it would silently rekey every price under it.
   */
  updateVersion(feeScheduleId: string, effectiveFrom: string, patch: { notes: string | null }) {
    return call<FeeScheduleVersionSummary>(feeScheduleVersionPath(feeScheduleId, effectiveFrom), {
      method: "PATCH",
      body: patch,
    });
  }

  removeVersion(feeScheduleId: string, effectiveFrom: string) {
    return call<{ removed: number }>(feeScheduleVersionPath(feeScheduleId, effectiveFrom), {
      method: "DELETE",
    });
  }

  /** The prices held by one dated version. */
  prices(feeScheduleId: string, effectiveFrom: string) {
    return call<FeeSchedulePrice[]>(feeScheduleLinesPath(feeScheduleId, effectiveFrom));
  }

  /** Makes one version price exactly these services, at these prices. */
  replacePrices(feeScheduleId: string, effectiveFrom: string, prices: FeeSchedulePriceDraft[]) {
    return call<FeeSchedulePrice[]>(feeScheduleLinesPath(feeScheduleId, effectiveFrom), {
      method: "PUT",
      body: { prices },
    });
  }

  /** One price — a fee or a frequency changed in the grid. */
  updatePrice(
    feeScheduleId: string,
    effectiveFrom: string,
    serviceId: string,
    patch: Partial<FeeSchedulePriceDraft>,
  ) {
    return call<FeeSchedulePrice>(
      `${feeScheduleLinesPath(feeScheduleId, effectiveFrom)}/${encodeURIComponent(serviceId)}`,
      { method: "PATCH", body: patch },
    );
  }

  removePrice(feeScheduleId: string, effectiveFrom: string, serviceId: string) {
    return call<{ service_id: string }>(
      `${feeScheduleLinesPath(feeScheduleId, effectiveFrom)}/${encodeURIComponent(serviceId)}`,
      { method: "DELETE" },
    );
  }
}

export const feeSchedules = new FeeSchedulesResource(FEE_SCHEDULES_PATH);

export const countries = new Resource<Country, CountryDraft>(COUNTRIES_PATH);
export const companies = new Resource<Company, CompanyDraft>(COMPANIES_PATH);
export const clientGroups = new Resource<ClientGroup, ClientGroupDraft>(CLIENT_GROUPS_PATH);

export type CompanyAfrFilingDraft = {
  period_end: string;
  afr_due_date: string;
  notes?: string | null;
};

/** What filing a return leaves behind: the year closed, the year opened, the row. */
export interface AfrFilingResult {
  filed: CompanyAfrFiling;
  next: CompanyAfrFiling;
  company: Pick<
    Company,
    | "afr_fy_label"
    | "afr_period_end"
    | "afr_due_date"
    | "afr_status"
    | "afr_last_filed_fy"
    | "afr_last_filing_date"
  >;
}

/**
 * The AFR history — every company's, or one company's.
 *
 * `list()` reads the cross-company collection, which is what the register page,
 * the dashboard widget and the history export all filter differently: by
 * `period_end` for a year's picture, by `afr_due_date` for a deadline window, by
 * `afr_filing_date` for what was actually filed in a period. None of those is
 * the default, so none is applied here.
 *
 * There is deliberately no `update` reaching `afr_filing_date`. Filing closes one
 * financial year *and* opens the next, so it goes through {@link file} — a patch
 * could do only the first half and would leave the register advertising a
 * deadline its own history says was already met.
 */
class AfrFilingsResource extends Resource<CompanyAfrFiling, Partial<CompanyAfrFiling>> {
  /** One company's history, newest financial year first. */
  forCompany(companyId: string) {
    return call<CompanyAfrFiling[]>(companyAfrFilingsPath(companyId));
  }

  /** Records a company's first financial year. */
  open(companyId: string, values: CompanyAfrFilingDraft) {
    return call<CompanyAfrFiling>(companyAfrFilingsPath(companyId), {
      method: "POST",
      body: values,
    });
  }

  /** Files the return, closing this year and opening the next. */
  file(
    companyId: string,
    filingId: string,
    values: { afr_filing_date: string; reference?: string | null; notes?: string | null },
  ) {
    return call<AfrFilingResult>(companyAfrFilePath(companyId, filingId), {
      method: "POST",
      body: values,
    });
  }

  /** Closes a year the registry required no return for, and opens the next. */
  waive(companyId: string, filingId: string, notes?: string | null) {
    return call<AfrFilingResult>(`${companyAfrFilingPath(companyId, filingId)}/waive`, {
      method: "POST",
      body: { notes: notes ?? null },
    });
  }

  /**
   * Un-files a return, removing the year it opened.
   *
   * Refused where that successor has itself been filed or has documents on it —
   * at which point the right correction is to the dates, not to the chain.
   */
  reopen(companyId: string, filingId: string) {
    return call<AfrFilingResult & { reopened: CompanyAfrFiling; removed: CompanyAfrFiling | null }>(
      `${companyAfrFilingPath(companyId, filingId)}/reopen`,
      { method: "POST", body: {} },
    );
  }

  /** Corrects a stored period — a year-end keyed wrongly, or an extension. */
  amend(companyId: string, filingId: string, patch: Partial<CompanyAfrFiling>) {
    return call<CompanyAfrFiling>(companyAfrFilingPath(companyId, filingId), {
      method: "PATCH",
      body: patch,
    });
  }

  /** Removes a financial year. Refused while documents hang off it. */
  discard(companyId: string, filingId: string) {
    return call<{ removed: CompanyAfrFiling }>(companyAfrFilingPath(companyId, filingId), {
      method: "DELETE",
    });
  }

  /**
   * Every document across companies, carrying its S3 key — the export's read.
   *
   * One request rather than one per financial year, which on a few hundred
   * years would be a few hundred round trips before the first row is written.
   */
  allDocuments() {
    return call<(AfrDocumentRow & { s3_key: string; download_url: string })[]>(AFR_DOCUMENTS_PATH);
  }

  /** The files on one financial year. The S3 key never comes back. */
  documents(companyId: string, filingId: string) {
    return call<AfrDocumentRow[]>(companyAfrDocumentsPath(companyId, filingId));
  }

  /**
   * Adds a file: grant, PUT the bytes straight to storage, register the row.
   *
   * All three steps here rather than in the page, because leaving them to the
   * caller is how a bucket ends up holding objects no row points at — the upload
   * succeeds, the registration is forgotten, and nothing on any screen says a
   * file is there.
   */
  async addDocument(
    companyId: string,
    filingId: string,
    file: File,
    meta: { kind: string; title: string; notes?: string | null },
  ) {
    // The type off the extension, not `file.type` — the browser leaves that
    // blank for an `.xlsx` on a machine without Office. An unknown extension
    // goes up as-is and is refused by the grant with the reason.
    const contentType = afrDocumentType(file.name)?.contentType ?? file.type;

    const grant = await call<{ url: string; key: string; expires_in: number }>(
      companyAfrDocumentUploadUrlPath(companyId, filingId),
      {
        method: "POST",
        body: { file_name: file.name, content_type: contentType, size_bytes: file.size },
      },
    );
    if (grant.error || !grant.data) return { data: null, error: grant.error };

    const put = await fetch(grant.data.url, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
    });
    if (!put.ok) {
      return {
        data: null,
        error: { message: `Upload failed (${put.status})`, code: "upload_failed" },
      };
    }

    return call<AfrDocumentRow>(companyAfrDocumentsPath(companyId, filingId), {
      method: "POST",
      body: {
        kind: meta.kind,
        title: meta.title,
        notes: meta.notes ?? null,
        file_name: file.name,
        content_type: contentType,
        size_bytes: file.size,
        s3_key: grant.data.key,
      },
    });
  }

  documentDownloadUrl(companyId: string, filingId: string, documentId: string) {
    return call<{ url: string; expires_in: number }>(
      companyAfrDocumentDownloadPath(companyId, filingId, documentId),
    );
  }

  removeDocument(companyId: string, filingId: string, documentId: string) {
    return call<{ id: string }>(
      `${companyAfrDocumentsPath(companyId, filingId)}/${encodeURIComponent(documentId)}`,
      { method: "DELETE" },
    );
  }
}

/** A document row as the API returns it — without the S3 key. */
export type AfrDocumentRow = Omit<CompanyAfrFilingDocument, "s3_key">;

export const afrFilings = new AfrFilingsResource(AFR_FILINGS_PATH);

/**
 * What a document attached to an AFR can be — reference data, not an enum.
 *
 * The list seeds itself on first read, so the picker is never empty on a stack
 * where nobody has run a script.
 */
export const afrDocumentKinds = new Resource<
  AfrDocumentKindRow,
  { name: string; is_active?: boolean }
>(AFR_DOCUMENT_KINDS_PATH);

class ContactsResource extends Resource<Contact, ContactDraft> {
  /**
   * The list with each contact's roles attached, in one request.
   *
   * The two narrowing options ask who is *attached* to a company or a client
   * group — by a role held there, or by having it as their primary affiliation. A
   * plain `.eq("company_id", …)` would only find the second kind, so a director at
   * three companies would be missing from two of those companies' own filters.
   * Passing both means both must hold.
   */
  listWithRoles(options: { servesCompanyId?: string; servesClientGroupId?: string } = {}) {
    const extra: Record<string, string> = { include: "roles" };
    if (options.servesCompanyId) extra.serves_company_id = options.servesCompanyId;
    if (options.servesClientGroupId) extra.serves_client_group_id = options.servesClientGroupId;
    return new ListBuilder<ContactWithRoles>(CONTACTS_PATH, extra);
  }

  roles(contactId: string) {
    return call<ContactRole[]>(`${CONTACTS_PATH}/${encodeURIComponent(contactId)}/roles`);
  }

  /** Replaces the contact's whole role set in one atomic write. */
  replaceRoles(contactId: string, roles: ContactRoleDraft[]) {
    return call<ContactRole[]>(`${CONTACTS_PATH}/${encodeURIComponent(contactId)}/roles`, {
      method: "PUT",
      body: { roles },
    });
  }
}

export const contacts = new ContactsResource(CONTACTS_PATH);

export type ServiceDraft = Partial<
  Omit<Service, "id" | "created_at" | "updated_at" | "created_by">
>;
export type ServiceCategoryDraft = Partial<
  Omit<ServiceCategory, "id" | "created_at" | "updated_at" | "created_by">
>;
export type BankAccountDraft = Partial<
  Omit<BankAccount, "id" | "created_at" | "updated_at" | "created_by">
>;

/** One schedule's rate for a service, as the catalogue dialog states it. */
export interface ServiceSchedulePriceDraft {
  fee_schedule_id: string;
  standard_fee: number;
  billing_frequency?: string | null;
}

/** What `priceInSchedules` reports back, one row per schedule written. */
export interface ServiceSchedulePriceResult {
  fee_schedule_id: string;
  service_id: string;
  /** The dates written, oldest first. */
  effective_froms: string[];
  versions: number;
}

class ServicesResource extends Resource<Service, ServiceDraft> {
  /**
   * Prices this service in each of these schedules, on **every** dated version
   * each one holds.
   *
   * The catalogue fee is the fallback for a service no schedule prices, so a new
   * service that nobody priced bills every client the same amount whatever they
   * negotiated. The dialog therefore asks for the rates where the service is, and
   * one request writes them — the alternative is a version editor per schedule.
   *
   * Every version, not just the newest, because a service the schedule has never
   * priced has no earlier rate to preserve: leaving the earlier versions alone
   * would bill the catalogue fee for every past period, which is a number nobody
   * agreed. On a service that *was* already priced this does rewrite history, which
   * is why the result names the dates it wrote.
   *
   * Additive. A schedule left out keeps whatever it had; unpricing one is still
   * `removePrice` on the version that holds it.
   */
  priceInSchedules(serviceId: string, prices: ServiceSchedulePriceDraft[]) {
    return call<ServiceSchedulePriceResult[]>(serviceSchedulesPath(serviceId), {
      method: "POST",
      body: { prices },
    });
  }
}

/**
 * The service catalogue.
 *
 * `remove` marks inactive rather than deletes: `active` goes false and the row stays.
 * Every price on every dated version of every fee schedule names a service by id,
 * as do company plans, work log entries and the lines of invoices already issued —
 * so removing the row would leave each of them pointing at nothing, and the API
 * holds no copy to put back. `active` is the flag the resolver and every picker
 * already respect, so an inactive service bills nothing new while the rows that
 * already name it keep reading. Bringing one back is `update(id, { active: true })`.
 */
export const services = new ServicesResource(SERVICES_PATH);

/**
 * The catalogue's headings, on their own resource.
 *
 * `remove` is refused while services are still filed under the category — the
 * server answers 409 rather than orphaning them, and `update(id, { active: false })`
 * is the way to take a heading out of the pickers without losing it.
 */
export const serviceCategories = new Resource<ServiceCategory, ServiceCategoryDraft>(
  SERVICE_CATEGORIES_PATH,
);
export type ServiceBundleDraft = Partial<
  Omit<ServiceBundle, "id" | "created_at" | "updated_at" | "created_by">
>;

/**
 * An item as the bundle editor saves it; `id` is kept so an unchanged row survives
 * the replace with its own id and `created_at`.
 *
 * `description` and `fee` are optional: left null, the service is worded and
 * priced the way it would be if picked by hand — the company's schedule, its
 * group's, or the catalogue rate.
 */
export type ServiceBundleItemDraft = {
  id?: string;
  service_id: string;
  quantity?: number;
  description?: string | null;
  fee?: number | null;
  sort_order?: number;
  notes?: string | null;
};

/** A bundle with its items attached, as `listWithItems` returns it. */
export type ServiceBundleWithItems = ServiceBundle & { items: ServiceBundleItemWithService[] };

class ServiceBundlesResource extends Resource<ServiceBundle, ServiceBundleDraft> {
  /**
   * The list with each bundle's items attached, in one request.
   *
   * What the bundles page needs for its "5 services" column, and what a picker
   * needs to expand without a second round trip per bundle.
   */
  listWithItems() {
    return new ListBuilder<ServiceBundleWithItems>(SERVICE_BUNDLES_PATH, { include: "items" });
  }

  /**
   * One bundle's items, each carrying the service it names.
   *
   * The service's name, invoice wording and published fee come joined, because
   * every screen that expands a bundle needs them to build a line — and the
   * alternative is a request per item or the whole catalogue in memory.
   */
  items(bundleId: string) {
    return call<ServiceBundleItemWithService[]>(serviceBundleItemsPath(bundleId));
  }

  /** Replaces the bundle's whole item set in one atomic write. */
  replaceItems(bundleId: string, items: ServiceBundleItemDraft[]) {
    return call<ServiceBundleItemWithService[]>(serviceBundleItemsPath(bundleId), {
      method: "PUT",
      body: { items },
    });
  }
}

/**
 * The sets of services that are normally delivered together.
 *
 * `remove` really deletes, where `services.remove` marks inactive: nothing stores a
 * bundle id once it has been expanded into lines, so a deleted bundle leaves
 * nothing pointing at a row that is gone. The items go with it.
 */
export const serviceBundles = new ServiceBundlesResource(SERVICE_BUNDLES_PATH);

export const bankAccounts = new Resource<BankAccount, BankAccountDraft>(BANK_ACCOUNTS_PATH);

export type RemittanceDefaultsDraft = Partial<
  Omit<RemittanceDefaults, "id" | "created_at" | "updated_at" | "created_by">
>;

/**
 * The beneficiary details every bank account shares.
 *
 * One row, so a page reads it with `list()` and takes the first — `create` writes
 * it the first time and the server answers 409 to a second one, `update(id, …)`
 * edits it after that.
 */
export const remittanceDefaults = new Resource<RemittanceDefaults, RemittanceDefaultsDraft>(
  REMITTANCE_DEFAULTS_PATH,
);

export type InvoiceSettingsDraft = Pick<InvoiceSettings, "bulk_link_ttl_hours">;
/** Invoicing's own settings — one row, patched in place. */
export const invoiceSettings = new Resource<InvoiceSettings, InvoiceSettingsDraft>(
  INVOICE_SETTINGS_PATH,
);

/** The register of bulk actions — read-only; the work each row describes writes it. */
export const bulkActions = new Resource<BulkAction, never>(BULK_ACTIONS_PATH);

export type InvoiceNoteDraft = Partial<
  Omit<InvoiceNote, "id" | "created_at" | "updated_at" | "created_by">
>;

class InvoiceNotesResource extends Resource<InvoiceNote, InvoiceNoteDraft> {
  /**
   * Puts the list in this order, in one request.
   *
   * `ids` is what is on screen, in the order a drag left it — not the whole
   * table — and the server permutes those notes within the positions they
   * already held, then renumbers the list densely. So a drag on page two cannot
   * reshuffle page one, and the numbers come back settled rather than guessed
   * here: the response is the list in its new order.
   *
   * It is one call rather than a `PATCH` per row because the notes an operator
   * ticked print on the invoice in this order. A half-applied renumbering is two
   * notes at the same position, and the document then numbers its clauses
   * differently from the screen that chose them.
   */
  reorder(ids: string[]) {
    return call<InvoiceNote[]>(invoiceNotesReorderPath, { method: "POST", body: { ids } });
  }
}

/**
 * The standard wording an invoice can tick on.
 *
 * `active` is what the picker on a new invoice filters by, so marking a note inactive
 * takes it out of circulation without touching the invoices that already print
 * it — `update(id, { active: false })`, which is the toggle in the table.
 */
export const invoiceNotes = new InvoiceNotesResource(INVOICE_NOTES_PATH);

/**
 * The work log: delivered services and recharged expenses.
 *
 * `invoice_id` is the "already billed" stamp and the server owns it, so it is
 * not part of either draft — the invoice writer sets it, and deleting an invoice
 * clears it. What a page does ask for is the unbilled set, which is a filter:
 * `workLog.events.list().eq("company_id", id).is("invoice_id", null)`.
 *
 * `bundle_name` is left out of the event draft for the same reason: a row says
 * which bundle it came from with `bundle_id`, and the server copies the name off
 * that bundle, so the stamp cannot name one bundle and read as another.
 */
export type ServiceEventDraft = Partial<
  Omit<
    ServiceEvent,
    "id" | "created_at" | "updated_at" | "created_by" | "invoice_id" | "bundle_name"
  >
>;
export type SundryExpenseDraft = Partial<
  Omit<SundryExpense, "id" | "created_at" | "updated_at" | "created_by" | "invoice_id">
>;

/**
 * What the work log can be narrowed by beyond its own columns.
 *
 * `clientGroupId` is not a column on either table and should not be: a row names
 * a company and the company names the group, so a copy here would be a second
 * place for the answer to go stale when a company moves between groups. It is
 * resolved on the server for the same reason the VIRRGIN register's is — the
 * largest group holds several hundred companies, and sending their ids back as
 * an `in` filter is a query string longer than API Gateway will carry.
 *
 * `coversServiceId` is the service filter anybody actually means. A bundle logged
 * grouped is one row with no `service_id` and its contents in `bundle_items`, so
 * `eq("service_id", id)` leaves every packaged delivery out of its own service's
 * list. The nested match is the server's to make.
 */
export interface WorkLogFilters {
  clientGroupId?: string;
  /** Events only: the row's own service, or one inside the bundle it was logged as. */
  coversServiceId?: string;
}

function workLogParams(filters: WorkLogFilters = {}): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.clientGroupId) params.client_group_id = filters.clientGroupId;
  if (filters.coversServiceId) params.covers_service_id = filters.coversServiceId;
  return params;
}

class WorkLogEventsResource extends Resource<ServiceEvent, ServiceEventDraft> {
  list(filters: WorkLogFilters = {}) {
    return super.list(workLogParams(filters));
  }
}

class WorkLogExpensesResource extends Resource<SundryExpense, SundryExpenseDraft> {
  list(filters: Omit<WorkLogFilters, "coversServiceId"> = {}) {
    return super.list(workLogParams(filters));
  }
}

export const workLog = {
  events: new WorkLogEventsResource(WORK_LOG_EVENTS_PATH),
  expenses: new WorkLogExpensesResource(WORK_LOG_EXPENSES_PATH),
};

/**
 * The VIRRGIN float: the registry fees drawn against it, and the deposits that
 * top it up.
 *
 * `invoice_id` and `row_hash` are not in the draft. The first is the "already
 * recovered" stamp, which only the invoice writer sets; the second is the
 * statement line's identity, computed where the statement is read. A deposit
 * recorded by hand is an ordinary `create` — it has no statement line behind it.
 */
export type VirrginTransactionDraft = Partial<
  Omit<
    VirrginTransaction,
    "id" | "created_at" | "updated_at" | "created_by" | "invoice_id" | "import_batch" | "row_hash"
  >
>;

/**
 * The register, its float, and the statement import.
 *
 * `list` takes one parameter of its own: `list({ client_group_id })` is every
 * fee drawn for anybody in that group. A transaction has no group column — it
 * names a company, and the company names the group — so the group is resolved
 * on the server. That is not only tidiness: the largest group on this register
 * holds several hundred companies, and sending their ids back as an `in` filter
 * is a query string longer than API Gateway will carry.
 */
class VirrginTransactionsResource extends Resource<VirrginTransaction, VirrginTransactionDraft> {
  /**
   * The float, totalled over every transaction rather than over a page.
   *
   * Also carries the unrecovered value grouped by company, which is the same
   * scan: the register screen shows the totals and the dashboard shows the
   * grouping, and asking twice would be two scans for one answer.
   */
  summary() {
    return call<VirrginSummary>(virrginSummaryPath);
  }

  /**
   * Reads a statement: which company each line belongs to, and which lines are
   * already stored.
   *
   * `dryRun` writes nothing and answers with what it would do, which is the
   * preview an operator approves. The commit is the same call with the same
   * rows, so the preview cannot describe one outcome and the write perform
   * another — and both resolve the companies and the duplicates server-side,
   * against a register and a history that no longer fit in a page.
   */
  import(rows: VirrginImportRow[], options: { dryRun?: boolean } = {}) {
    return call<VirrginImportResult>(virrginImportPath, {
      method: "POST",
      body: { rows, dry_run: options.dryRun === true },
    });
  }

  /**
   * Tries the stored registry numbers against the register again — what to run
   * once the companies a statement named have themselves been imported.
   *
   * Fills blanks only, so it never undoes a company somebody attached by hand,
   * and is safe to run twice.
   */
  rematch() {
    return call<VirrginRematchResult>(virrginRematchPath, { method: "POST" });
  }
}

export const virrginTransactions = new VirrginTransactionsResource(VIRRGIN_TRANSACTIONS_PATH);

/**
 * An invoice is created by `POST /invoices` with its lines in the same call, so
 * `create` is not part of this resource — see `createInvoice` in `./api`. What
 * is here is the register and the lines.
 *
 * `invoice_number`, `company_id`, `subtotal` and `total` are missing on purpose:
 * the number comes from the counter, the company is fixed at creation, and the
 * money is the sum of the lines, which `replaceLines` re-totals server-side.
 */
export type InvoicePatch = Partial<
  Pick<
    Invoice,
    | "invoice_date"
    | "due_date"
    | "bill_to_contact_id"
    | "bank_account_id"
    | "note_ids"
    | "status"
    | "period_label"
    | "notes"
    // What the document says where it differs from the records behind it.
    | "terms"
    | "bill_to_name"
    | "bill_to_for"
    | "bill_to_address"
    | "from_name"
    | "from_address"
  >
>;

/** A line as the edit screen saves it; `id` keeps an existing line's identity. */
export type InvoiceLineDraft = Partial<
  Pick<InvoiceLine, "id" | "description" | "quantity" | "unit_amount" | "service_id">
>;

class InvoicesResource extends Resource<Invoice, InvoicePatch> {
  lines(invoiceId: string) {
    return call<InvoiceLine[]>(`${INVOICES_PATH}/${encodeURIComponent(invoiceId)}/lines`);
  }

  /**
   * The invoices an export covers, one row per line, names already resolved.
   *
   * A `ListBuilder` over the same filters `list()` takes, so "export what the
   * filters describe" is this with the page's filters on it and "export
   * everything" is this with none — one endpoint, and no second definition of
   * what the filters mean to drift from the list.
   *
   * It still needs `.all()`: the list endpoints answer with one page when no
   * limit is sent, and a file that stopped at ten rows is the bug this is here
   * to avoid.
   */
  exportLines() {
    return new ListBuilder<InvoiceExportLine>(`${INVOICES_PATH}/export`);
  }

  /**
   * Replaces the invoice's whole line set and re-totals the header in one call.
   *
   * The totals come back on the invoice in the response rather than being worked
   * out here, so the figure the screen shows is the one that was stored.
   */
  replaceLines(invoiceId: string, lines: InvoiceLineDraft[]) {
    return call<{ invoice: Invoice | null; lines: InvoiceLine[] }>(
      `${INVOICES_PATH}/${encodeURIComponent(invoiceId)}/lines`,
      { method: "PUT", body: { lines } },
    );
  }
}

/** `create` is typed away: an invoice is raised through `createInvoice` in `./api`. */
export const invoices: Omit<InvoicesResource, "create"> = new InvoicesResource(INVOICES_PATH);

/**
 * Grouped so a page can say `api.companies.list()` without the import colliding
 * with the local `companies` a component almost always already has.
 */

// ---------------------------------------------------------------------------
// Payroll, leave and attendance
// ---------------------------------------------------------------------------

export type EmployeeDraft = Partial<Omit<Employee, "id" | "created_at" | "updated_at">>;
export type EmployeeContractDraft = Partial<
  Omit<EmployeeContract, "id" | "employee_id" | "created_at" | "updated_at" | "created_by">
>;

/**
 * One person, as the merged form saves them.
 *
 * The person's own columns, their terms under `contract`, and two things that
 * are neither: `invite`, which mints the sign-in account for the address on the
 * record, and `roles`, the access it starts with. The server splits it back
 * across the two rows and does both side effects in the same request, which is
 * what makes "add somebody" one action instead of three screens.
 */
export type PersonDraft = EmployeeDraft & {
  contract?: EmployeeContractDraft;
  /** Mint the sign-in account for this person's address and link it. */
  invite?: boolean;
  /** The roles to grant it. Administrators only; omitted means the default. */
  roles?: AppRole[];
};
export type LeaveTypeDraft = Partial<Omit<LeaveType, "id" | "created_at" | "updated_at">>;
export type HolidayDraft = Partial<Omit<Holiday, "id" | "created_at" | "updated_at">>;
export type PayrollRateDraft = Partial<Omit<PayrollRate, "id" | "created_at" | "updated_at">>;
export type OfficeNetworkDraft = Partial<Omit<OfficeNetwork, "id" | "created_at" | "updated_at">>;
export type LeaveAllowanceDraft = Partial<Omit<LeaveAllowance, "id" | "created_at" | "updated_at">>;
export type PayPeriodDraft = Partial<Omit<PayPeriod, "id" | "created_at" | "updated_at">>;
export type PayrollFilingDraft = Partial<
  Pick<
    PayrollFiling,
    "status" | "filed_on" | "paid_on" | "reference" | "payment_reference" | "notes"
  >
>;
export type PayrollFilingDocumentDraft = Partial<
  Pick<PayrollFilingDocument, "kind" | "title" | "notes">
>;
export type EmployeePayItemDraft = Partial<
  Omit<EmployeePayItem, "id" | "created_at" | "updated_at">
>;

/** What the contract takes home, computed server-side so it is never a second stored figure. */
export interface NetPreview extends PayslipBreakdown {
  employee_id: string;
  annual_gross: number;
  period_gross: number;
  basis: string;
}

class EmployeesResource extends Resource<Person, PersonDraft> {
  /**
   * Everybody, with their access and — where the caller may read it — their
   * terms, plus the accounts that have nobody behind them.
   *
   * Its own method rather than `list()`, because the envelope carries something
   * a list does not: `unlinked_accounts` is the pool accounts no employee holds,
   * which is the one state the design does not otherwise permit and the thing
   * the page offers to settle. A finite list that closes, not a reconciliation
   * to be run forever.
   */
  async people(options: { page?: number; pageSize?: number } = {}) {
    const params = new URLSearchParams();
    if (options.pageSize != null) {
      params.set("limit", String(options.pageSize));
      params.set("offset", String(Math.max(0, ((options.page ?? 1) - 1) * options.pageSize)));
    }
    const search = params.toString();
    const path = search ? `${EMPLOYEES_PATH}?${search}` : EMPLOYEES_PATH;

    /*
     * `apiFetch` rather than `call`, because this response carries one key
     * beyond the standard envelope and `call` narrows to the standard one.
     */
    try {
      const result = await apiFetch<Person[]>(path);
      const envelope = result as typeof result & { unlinked_accounts?: Account[] };
      return {
        people: result.data ?? [],
        total: result.count ?? result.data?.length ?? 0,
        unlinked: envelope.unlinked_accounts ?? [],
        error: null,
      };
    } catch (error) {
      return { people: [], total: 0, unlinked: [], error: toError(error) };
    }
  }

  /**
   * The signed-in person's own record, terms and all.
   *
   * The one salary somebody is always entitled to see is their own, so this
   * answers with the contract whatever their roles. Null means they have an
   * account but no employee record, which the pages read as "nothing of your
   * own to show" rather than as an error.
   */
  me() {
    return call<Person | null>(`${EMPLOYEES_PATH}/me`);
  }

  /** The terms on their own, for the panel that only needs those. */
  contract(employeeId: string) {
    return call<EmployeeContract | null>(employeeContractPath(employeeId));
  }

  /** Saves the terms without touching the person. */
  saveContract(employeeId: string, patch: EmployeeContractDraft) {
    return call<EmployeeContract>(employeeContractPath(employeeId), {
      method: "PATCH",
      body: patch,
    });
  }

  /**
   * Mints the sign-in account for somebody who already has a record.
   *
   * Cognito mails the temporary password and the account is linked in the same
   * request — the whole reason there is nothing left to reconcile afterwards.
   */
  inviteAccount(employeeId: string) {
    return call<Account>(employeeAccountPath(employeeId), { method: "POST" });
  }

  /** Takes the sign-in and its roles away, and keeps the person. */
  removeAccount(employeeId: string) {
    return call<{ employee_id: string; user_id: string }>(employeeAccountPath(employeeId), {
      method: "DELETE",
    });
  }

  grantRole(employeeId: string, role: AppRole) {
    return call(employeeRolesPath(employeeId), { method: "POST", body: { role } });
  }

  revokeRole(employeeId: string, role: AppRole) {
    return call(employeeRolesPath(employeeId), { method: "DELETE", body: { role } });
  }

  /**
   * Gives an account that already exists the person it belongs to.
   *
   * Not a link but a creation: what the account was missing is a record, and
   * this makes one with `user_id` already set, so what comes out the other end
   * is an ordinary employee rather than a special case.
   */
  adoptAccount(values: PersonDraft & { user_id: string }) {
    return call<Person>(employeeAdoptAccountPath, { method: "POST", body: values });
  }

  /** This employee's recurring additions and deductions. */
  payItems(employeeId: string) {
    return call<EmployeePayItem[]>(employeePayItemsPath(employeeId));
  }

  addPayItem(employeeId: string, values: EmployeePayItemDraft) {
    return call<EmployeePayItem>(employeePayItemsPath(employeeId), {
      method: "POST",
      body: values,
    });
  }

  updatePayItem(employeeId: string, itemId: string, patch: EmployeePayItemDraft) {
    return call<EmployeePayItem>(
      `${employeePayItemsPath(employeeId)}/${encodeURIComponent(itemId)}`,
      { method: "PATCH", body: patch },
    );
  }

  removePayItem(employeeId: string, itemId: string) {
    return call<EmployeePayItem>(
      `${employeePayItemsPath(employeeId)}/${encodeURIComponent(itemId)}`,
      { method: "DELETE" },
    );
  }

  /** Documents held about this employee — contracts, permits, identification. */
  documents(employeeId: string) {
    return call<EmployeeDocument[]>(employeeDocumentsPath(employeeId));
  }

  /**
   * Uploads a file and registers it, in the two steps the API expects.
   *
   * The bytes go straight from the browser to S3 against a presigned PUT, so
   * nothing is bounded by the API's request limit and a scanned passport is as
   * ordinary as a one-page letter. The row is registered only once the upload
   * has actually succeeded — the other order would leave a document in the list
   * that cannot be downloaded.
   */
  async uploadDocument(
    employeeId: string,
    file: File,
    meta: {
      kind: EmployeeDocumentKind;
      title: string;
      issued_on?: string | null;
      expires_on?: string | null;
      notes?: string | null;
    },
  ) {
    const grant = await call<{ url: string; key: string; expires_in: number }>(
      employeeDocumentUploadUrlPath(employeeId),
      {
        method: "POST",
        body: { file_name: file.name, content_type: file.type, size_bytes: file.size },
      },
    );
    if (grant.error || !grant.data)
      return { data: null, error: grant.error, count: null, page: null };

    const put = await fetch(grant.data.url, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!put.ok) {
      return {
        data: null,
        error: { message: `The upload was refused by storage (${put.status})` },
        count: null,
        page: null,
      };
    }

    return call<EmployeeDocument>(employeeDocumentsPath(employeeId), {
      method: "POST",
      body: {
        ...meta,
        file_name: file.name,
        content_type: file.type,
        size_bytes: file.size,
        s3_key: grant.data.key,
      },
    });
  }

  /** A short-lived link to read one document. */
  documentUrl(employeeId: string, documentId: string) {
    return call<{ url: string; expires_in: number; file_name: string }>(
      employeeDocumentDownloadPath(employeeId, documentId),
    );
  }

  removeDocument(employeeId: string, documentId: string) {
    return call<EmployeeDocument>(
      `${employeeDocumentsPath(employeeId)}/${encodeURIComponent(documentId)}`,
      { method: "DELETE" },
    );
  }

  /** Every leave type's balance for a year, worked out from the attendance grid. */
  leaveBalances(employeeId: string, year: number, asOf?: string) {
    const search = new URLSearchParams({ year: String(year) });
    if (asOf) search.set("as_of", asOf);
    return call<EmployeeLeaveBalances>(`${employeeLeaveBalancesPath(employeeId)}?${search}`);
  }

  /**
   * Gross to net for this contract.
   *
   * Asked of the server rather than worked out in the form, so the figure the
   * employee is quoted comes from the same code that will produce their payslip.
   */
  netPreview(employeeId: string) {
    return call<NetPreview>(`${EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/net-preview`);
  }
}

export const employees = new EmployeesResource(EMPLOYEES_PATH);

export interface AllowanceRowDraft {
  leave_type_id: string;
  entitlement_days: number;
  carried_in_days?: number;
  adjustment_days?: number;
  /**
   * How many of those days carry pay for this employee, where that is not the
   * type's own figure. Null or omitted inherits the type's.
   */
  paid_days_limit?: number | null;
  adjustment_reason?: string | null;
}

/**
 * One employee's entitlements for a year, set as a set.
 *
 * A type left out of the list has its override removed and falls back to the
 * leave type's own default, which is how somebody is put back on the standard
 * allowance without a delete from the browser.
 */
export function setLeaveAllowances(input: {
  employeeId: string;
  year: number;
  allowances: AllowanceRowDraft[];
}) {
  return call<EmployeeLeaveBalances>(leaveAllowancesSetPath, {
    method: "PUT",
    body: {
      employee_id: input.employeeId,
      year: input.year,
      allowances: input.allowances,
    },
  });
}
export const leaveTypes = new Resource<LeaveType, LeaveTypeDraft>(LEAVE_TYPES_PATH);
export const holidays = new Resource<Holiday, HolidayDraft>(HOLIDAYS_PATH);
/**
 * The statutory rates, as the one record they are.
 *
 * Not a `Resource`: there is no list to page, no id to address and nothing to
 * delete. `get` answers with the record or `null` when it has never been saved,
 * and `save` writes the whole of it.
 */
export const payrollRates = {
  get() {
    return call<PayrollRate | null>(PAYROLL_RATES_PATH);
  },
  save(values: PayrollRateDraft) {
    return call<PayrollRate>(PAYROLL_RATES_PATH, { method: "PUT", body: values });
  },
};
export const officeNetworks = new Resource<OfficeNetwork, OfficeNetworkDraft>(OFFICE_NETWORKS_PATH);
export const leaveAllowances = new Resource<LeaveAllowance, LeaveAllowanceDraft>(
  LEAVE_ALLOWANCES_PATH,
);
class PayrollFilingsResource extends Resource<PayrollFiling, PayrollFilingDraft> {
  /** The files behind one return — the form, the receipt, the acknowledgement. */
  documents(filingId: string) {
    return call<PayrollFilingDocument[]>(payrollFilingDocumentsPath(filingId));
  }

  /**
   * Uploads a file and registers it, in the two steps the API expects.
   *
   * The bytes go straight from the browser to S3 against a presigned PUT, so
   * nothing is bounded by the API's request limit. The row is registered only
   * once the upload has actually succeeded — the other order would leave a
   * document in the list that cannot be downloaded.
   */
  async uploadDocument(
    filingId: string,
    file: File,
    meta: { kind: FilingDocumentKind; title: string; notes?: string | null },
  ) {
    const grant = await call<{ url: string; key: string; expires_in: number }>(
      payrollFilingDocumentUploadUrlPath(filingId),
      {
        method: "POST",
        body: { file_name: file.name, content_type: file.type, size_bytes: file.size },
      },
    );
    if (grant.error || !grant.data)
      return { data: null, error: grant.error, count: null, page: null };

    const put = await fetch(grant.data.url, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!put.ok) {
      return {
        data: null,
        error: { message: `The upload was refused by storage (${put.status})` },
        count: null,
        page: null,
      };
    }

    return call<PayrollFilingDocument>(payrollFilingDocumentsPath(filingId), {
      method: "POST",
      body: {
        ...meta,
        file_name: file.name,
        content_type: file.type,
        size_bytes: file.size,
        s3_key: grant.data.key,
      },
    });
  }

  /** A short-lived link to read one of them. */
  documentUrl(filingId: string, documentId: string) {
    return call<{ url: string; expires_in: number; file_name: string }>(
      payrollFilingDocumentDownloadPath(filingId, documentId),
    );
  }

  removeDocument(filingId: string, documentId: string) {
    return call<PayrollFilingDocument>(
      `${payrollFilingDocumentsPath(filingId)}/${encodeURIComponent(documentId)}`,
      { method: "DELETE" },
    );
  }
}

export const payrollFilings = new PayrollFilingsResource(PAYROLL_FILINGS_PATH);

export interface LeaveRequestDraft {
  employee_id: string;
  leave_type_id: string;
  start_date: string;
  end_date: string;
  start_half?: DayHalf;
  end_half?: DayHalf;
  reason?: string | null;
}

/**
 * A leave request as `GET /leave-requests` returns it.
 *
 * The list resolves the employee's name server-side, because the screens that
 * draw it cannot read `employees` — the contract is on that row, and approving a
 * day off should not require being trusted with somebody's salary. Only the name
 * travels, so a column of ids is not the alternative.
 */
export interface LeaveRequestRow extends LeaveRequest {
  employee_name: string | null;
}

class LeaveRequestsResource extends Resource<LeaveRequest, LeaveRequestDraft> {
  /**
   * The requests this caller may see: their own, their team's, or everybody's.
   *
   * Who you are decides the set — the filters below only narrow inside it, and
   * cannot widen it. They are sent as plain parameters rather than through the
   * generic `eq.` filter grammar because `/leave-requests` answers with a list
   * of its own, which reads them by name; the generic list would happily hand
   * the whole company's leave to anybody who asked for it.
   */
  listRows(filters: { employee_id?: string; status?: LeaveRequestStatus } = {}) {
    const extra: Record<string, string> = {};
    if (filters.employee_id) extra.employee_id = filters.employee_id;
    if (filters.status) extra.status = filters.status;
    return new ListBuilder<LeaveRequestRow>(LEAVE_REQUESTS_PATH, extra);
  }

  /**
   * Approving is a call of its own, not a status patch.
   *
   * It re-checks the balance as it stands now, splits the days at the type's
   * paid limit, and writes them onto the attendance grid. `approveExcessAsUnpaid`
   * is how a request past the allowance is granted deliberately rather than
   * becoming a negative balance nobody chose.
   */
  approve(id: string, options: { approveExcessAsUnpaid?: boolean; note?: string } = {}) {
    return call<{
      request: LeaveRequest;
      days_written: number;
      days: number;
      split: string | null;
    }>(`${LEAVE_REQUESTS_PATH}/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      body: { approve_excess_as_unpaid: options.approveExcessAsUnpaid, note: options.note },
    });
  }

  reject(id: string, note?: string) {
    return call<LeaveRequest>(`${LEAVE_REQUESTS_PATH}/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      body: { note },
    });
  }

  /** Takes the half-days back off the grid, which is what credits the allowance. */
  cancel(id: string, note?: string) {
    return call<LeaveRequest>(`${LEAVE_REQUESTS_PATH}/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: { note },
    });
  }
}

export const leaveRequests = new LeaveRequestsResource(LEAVE_REQUESTS_PATH);

export interface WfhRequestDraft {
  employee_id: string;
  start_date: string;
  end_date: string;
  /** `wfh` unless the days are at a client's site, travelling or training. */
  proposes_status?: AttendanceStatus;
  reason?: string | null;
}

/** A work-from-home request as `GET /wfh-requests` returns it, name resolved. */
export interface WfhRequestRow extends WfhRequest {
  employee_name: string | null;
}

/**
 * Asking to work away from the office.
 *
 * Shaped like `leaveRequests` on purpose — the same list, the same three
 * decisions — because to the person asking it is the same act, and the leave
 * screens draw both through one table. What differs is behind `approve`: this
 * one writes **nothing** onto the attendance grid. Permission to work from home
 * is not a day worked, so the day still has to be clocked; approving it onto the
 * grid would pay a Monday nobody turned up for. See
 * `docs/work-from-home-approval.md` §3.
 */
class WfhRequestsResource extends Resource<WfhRequest, WfhRequestDraft> {
  /** Yours, your team's, or everybody's — the server decides which. */
  listRows(filters: { employee_id?: string; status?: LeaveRequestStatus } = {}) {
    const extra: Record<string, string> = {};
    if (filters.employee_id) extra.employee_id = filters.employee_id;
    if (filters.status) extra.status = filters.status;
    return new ListBuilder<WfhRequestRow>(WFH_REQUESTS_PATH, extra);
  }

  /**
   * Grants the permission, and nothing else.
   *
   * No balance to check and no allowance to draw down: this is a day worked.
   * The server re-counts the working days it covers and refuses days inside a
   * closed period, then answers with the sentence worth repeating to the
   * employee — the days still have to be clocked.
   */
  approve(id: string, note?: string) {
    return call<WfhRequest>(wfhRequestDecisionPath(id, "approve"), {
      method: "POST",
      body: { note },
    });
  }

  reject(id: string, note?: string) {
    return call<WfhRequest>(wfhRequestDecisionPath(id, "reject"), {
      method: "POST",
      body: { note },
    });
  }

  /** Nothing to take back off the grid; days already worked under it stand. */
  cancel(id: string, note?: string) {
    return call<WfhRequest>(wfhRequestDecisionPath(id, "cancel"), {
      method: "POST",
      body: { note },
    });
  }
}

export const wfhRequests = new WfhRequestsResource(WFH_REQUESTS_PATH);

export interface AttendanceGridDay extends AttendanceDay {
  /**
   * The public holiday's own name, when the date is one in this employee's
   * territory — so a calendar can print "Territory Day" rather than only
   * colouring the square.
   */
  holiday_name: string | null;
  /** True when nothing is stored and this is what the rollup would say. */
  proposed: boolean;
}

/**
 * One person, as the attendance screens are allowed to know them.
 *
 * Name, number and job title: enough to pick somebody out of a list, and
 * nothing from the contract on the same row.
 */
export interface AttendancePerson {
  id: string;
  full_name: string;
  employee_no: string | null;
  job_title: string | null;
  status: string;
}

export interface AttendanceGridRow {
  employee: {
    id: string;
    full_name: string;
    employee_no: string | null;
    job_title: string | null;
    status: string;
  };
  days: AttendanceGridDay[];
  totals: {
    unpaid_halves: number;
    worked_halves: number;
    leave_halves_by_type: Record<string, number>;
    absence_halves: number;
    unconfirmed_halves: number;
    non_working_halves: number;
  };
}

/** One clock-in and clock-out, as the timesheet shows it. */
export interface TimesheetEntry extends TimeEntry {
  /** Whether this entry's time counts towards the day. */
  counted: boolean;
  /** Minutes it spans — up to now while it is still open. */
  minutes: number;
}

/**
 * One date on the timesheet.
 *
 * Every date in the window has a row, including the ones nobody clocked: the
 * day somebody forgot is exactly the row that has to be visible. `am_status`
 * and `pm_status` are what the grid says about it, so a day of leave reads as
 * leave rather than as missing hours.
 */
export interface TimesheetDay extends DayEffort {
  weekday: number;
  /** Whether work was due at all, which is what makes a nil row ordinary. */
  working_day: boolean;
  holiday: boolean;
  am_status: AttendanceStatus | null;
  am_leave_type_id: string | null;
  pm_status: AttendanceStatus | null;
  pm_leave_type_id: string | null;
  note: string | null;
  locked: boolean;
  clocked: TimesheetEntry[];
}

export interface TimesheetRow {
  employee: {
    id: string;
    full_name: string;
    employee_no: string | null;
    job_title: string | null;
    status: string;
  };
  /** Their own working day — what the hours are measured against. */
  work_day: WorkDayWindow;
  /** Minutes one contracted day runs, start to end. */
  contracted_minutes: number;
  days: TimesheetDay[];
  totals: EffortTotals & {
    working_days: number;
    /** What the contract asked for across the window, to compare against. */
    contracted_minutes: number;
  };
}

export interface OpenQuestion {
  employee_id: string;
  employee_name: string;
  date: string;
  half: DayHalf;
  reason: string;
  locked: boolean;
}

/**
 * The attendance grid — one request for the whole team and the whole month,
 * because that is what the worksheet it replaces was.
 */
export const attendance = {
  /**
   * Whose attendance the caller may open, searched server-side.
   *
   * Asked without a `q`, `total` is how many people they can see at all, which
   * is what decides whether a screen offers a choice of person or simply shows
   * the caller their own month.
   */
  people(options: { q?: string; limit?: number; employeeId?: string } = {}) {
    const search = new URLSearchParams();
    if (options.q) search.set("q", options.q);
    if (options.limit) search.set("limit", String(options.limit));
    if (options.employeeId) search.set("employee_id", options.employeeId);
    const query = search.toString();

    return call<{ me: string | null; total: number; people: AttendancePerson[] }>(
      query ? `${attendancePeoplePath}?${query}` : attendancePeoplePath,
    );
  },

  grid(from: string, to: string, employeeId?: string) {
    const search = new URLSearchParams({ from, to });
    if (employeeId) search.set("employee_id", employeeId);
    return call<{ from: string; to: string; employees: AttendanceGridRow[] }>(
      `${ATTENDANCE_PATH}?${search}`,
    );
  },

  /** Declare or correct one half-day, or the whole day at once. */
  setDay(
    employeeId: string,
    date: string,
    patch: {
      status?: AttendanceStatus;
      am_status?: AttendanceStatus;
      pm_status?: AttendanceStatus;
      leave_type_id?: string | null;
      am_leave_type_id?: string | null;
      pm_leave_type_id?: string | null;
      note?: string | null;
    },
  ) {
    return call<AttendanceDay>(attendanceDayPath(employeeId, date), {
      method: "PATCH",
      body: patch,
    });
  },

  approveDay(employeeId: string, date: string) {
    return call<AttendanceDay>(`${attendanceDayPath(employeeId, date)}/approve`, {
      method: "POST",
    });
  },

  /** Re-propose a window. Only ever fills in days nobody has answered. */
  rollup(from?: string, to?: string, employeeId?: string) {
    return call<{
      from: string;
      to: string;
      employees: number;
      days_proposed: number;
      halves_left_alone: number;
      open_questions: number;
      /** The first 200 of them, each naming whose day it is. */
      questions: { employee_id: string; employee_name: string; date: string; half: string }[];
    }>(`${ATTENDANCE_PATH}/rollup`, {
      method: "POST",
      body: { from, to, employee_id: employeeId },
    });
  },

  /**
   * "I am working."
   *
   * The whole of the presence capture: a windowed record that this person's
   * account was active, which is enough to show a day was worked and never
   * enough on its own to decide one. Nothing is read about where from.
   */
  heartbeat() {
    return call<{ recorded: boolean }>(`${ATTENDANCE_PATH}/heartbeat`, {
      method: "POST",
      body: {},
    });
  },

  /** This person's clock: whether they are in, today's entries, hours so far. */
  clock() {
    return call<{
      employee_id: string;
      employee_name: string;
      date: string;
      clocked_in: boolean;
      open_entry: TimeEntry | null;
      entries: TimeEntry[];
      worked: {
        am: boolean;
        pm: boolean;
        am_minutes: number;
        pm_minutes: number;
        total_minutes: number;
      };
      pending: boolean;
    } | null>(`${ATTENDANCE_PATH}/clock`);
  },

  /**
   * Start work. Never refused — but `counted` says whether it amounted to
   * anything, and `message` says why in words meant for the person who pressed it.
   */
  clockIn() {
    return call<{
      entry: TimeEntry;
      counted: boolean;
      verdict: string;
      message: string;
    }>(`${ATTENDANCE_PATH}/clock-in`, { method: "POST", body: {} });
  },

  /**
   * Stop work. Judged the same way as clocking in, because both ends have to
   * stand up: an entry that counted all morning stops counting if it ended
   * somewhere nobody recognises. The hours are kept either way.
   */
  clockOut() {
    return call<{
      entry: TimeEntry;
      minutes: number | null;
      counted: boolean;
      verdict: string;
      day: AttendanceDay | null;
      message: string;
    }>(`${ATTENDANCE_PATH}/clock-out`, { method: "POST", body: {} });
  },

  /**
   * The same window as the grid, in hours: every date, every clock-in and
   * clock-out on it, what the day added up to and what the month did.
   *
   * Counted and uncounted minutes come back apart. Time clocked outside
   * somebody's working pattern is recorded in full and counts for nothing until
   * a manager says what it was, and one total would hide the hours worth asking
   * about.
   */
  timesheet(from: string, to: string, employeeId?: string) {
    const search = new URLSearchParams({ from, to });
    if (employeeId) search.set("employee_id", employeeId);
    return call<{ from: string; to: string; employees: TimesheetRow[] }>(
      `${attendanceTimesheetPath}?${search}`,
    );
  },

  /**
   * A manager settling one clocked entry nothing else accounts for.
   *
   * `resolve` accepts the time and names where it was worked from — which is
   * the thing the clock does not know. `reject` records that it was not worked,
   * and leaves the entry saying so rather than deleting it.
   */
  settleEntry(
    decision: "resolve" | "reject",
    employeeId: string,
    startedAt: string,
    body: { status?: AttendanceStatus; note?: string | null } = {},
  ) {
    return call<{ entry: TimeEntry; day: AttendanceDay | null; message: string }>(
      attendanceEntryDecisionPath(decision),
      { method: "POST", body: { employee_id: employeeId, started_at: startedAt, ...body } },
    );
  },

  /** What nobody has answered yet — the list that blocks a period from closing. */
  openQuestions(from: string, to: string) {
    const search = new URLSearchParams({ from, to });
    return call<{ from: string; to: string; questions: OpenQuestion[] }>(
      `${ATTENDANCE_PATH}/open-questions?${search}`,
    );
  },

  /**
   * The pay run's own list of what stops it, for the people it would take —
   * including working days with nothing stored, which `openQuestions` skips.
   */
  unanswered(from: string, to: string) {
    const search = new URLSearchParams({ from, to });
    return call<{ from: string; to: string; days: UnansweredDay[] }>(
      `${ATTENDANCE_PATH}/unanswered?${search}`,
    );
  },

  /**
   * Answers every unanswered day of one person's stretch at once. Answered and
   * locked days are left as they are.
   */
  settle(body: {
    employee_id: string;
    from: string;
    to: string;
    status: AttendanceStatus;
    leave_type_id?: string | null;
    note?: string | null;
  }) {
    return call<{ days_settled: number; days_locked: number }>(`${ATTENDANCE_PATH}/settle`, {
      method: "POST",
      body,
    });
  },
};

/** One half-day a pay run would refuse over. A day with no record is its `am`. */
export interface UnansweredDay {
  employee_id: string;
  employee_name: string;
  date: string;
  half: "am" | "pm";
}

export interface PayRunResult {
  period: PayPeriod;
  totals: {
    gross: number;
    payroll_tax_employee: number;
    payroll_tax_employer: number;
    payroll_tax_total: number;
    nhi_employee: number;
    nhi_employer: number;
    nhi_total: number;
    ssb_employee: number;
    ssb_employer: number;
    ssb_total: number;
    net: number;
    employer_cost: number;
    headcount: number;
  };
  payslips: (Payslip & { unpaid_days: number; not_employed_days: number })[];
  warnings: string[];
}

class PayPeriodsResource extends Resource<PayPeriod, PayPeriodDraft> {
  /**
   * Read the grid and write the payslips.
   *
   * Refused outright while any half-day in the period is unanswered — the error
   * carries the list in `details`, so the screen can link straight to each one.
   * `preview` computes and returns without writing, through the same code path,
   * so what is reviewed is what gets stored. `employeeIds` narrows the run to
   * those people, and the period then holds only their payslips.
   */
  calculate(id: string, options: { preview?: boolean; employeeIds?: string[] } = {}) {
    const search = options.preview ? "?preview=true" : "";
    return call<PayRunResult>(`${payPeriodActionPath(id, "calculate")}${search}`, {
      method: "POST",
      // Left out, the run is every active employee; sent, it is just these.
      body: options.employeeIds ? { employee_ids: options.employeeIds } : {},
    });
  }

  /** Locks the period and every attendance day it read. */
  approve(id: string) {
    return call<PayPeriod>(payPeriodActionPath(id, "approve"), { method: "POST", body: {} });
  }

  /** Refused once a return has been filed; a reason is required. */
  reopen(id: string, reason: string) {
    return call<PayPeriod>(payPeriodActionPath(id, "reopen"), { method: "POST", body: { reason } });
  }

  payslips(id: string) {
    return call<{ period: PayPeriod; payslips: Payslip[] }>(payPeriodPayslipsPath(id));
  }
}

export const payPeriods = new PayPeriodsResource(PAY_PERIODS_PATH);

/**
 * A payslip as the list answers with it: the stored row, plus the period it
 * belongs to.
 *
 * The period's label and dates are joined server-side because the table is
 * read a month at a time and `pay_periods` is closed to an employee — they may
 * read their own payslip, not the register of runs it came from.
 */
export interface PayslipRow extends Payslip {
  period_label: string | null;
  period_start: string | null;
  period_end: string | null;
  period_status: PayPeriodStatus | null;
  period_pay_date: string | null;
}

/** Your own payslips, or everybody's — the server decides which from your roles. */
export const payslips = {
  /**
   * Everything the payslip document prints, assembled server-side.
   *
   * It joins the employee's statutory identifiers and the period's pinned rates,
   * neither of which the browser may read, and returns only the fields that
   * belong on the document.
   */
  document(payslipId: string) {
    return call<PayslipPdfData>(`${PAYSLIPS_PATH}/${encodeURIComponent(payslipId)}/document`);
  },

  /**
   * One page of payslips, newest month first.
   *
   * Paged by the API rather than in the browser: the list grows by a row per
   * employee per month and never shrinks, so "every payslip there has ever
   * been" is the one read on this screen that gets slower every month it is
   * used. `count` on the result is the whole matching set, which is what the
   * footer counts against.
   */
  list(
    filters: {
      employeeId?: string;
      payPeriodId?: string;
      /** The pay periods of one payroll year. */
      year?: number;
      page?: number;
      pageSize?: number;
    } = {},
  ) {
    const search = new URLSearchParams();
    if (filters.employeeId) search.set("employee_id", filters.employeeId);
    if (filters.payPeriodId) search.set("pay_period_id", filters.payPeriodId);
    if (filters.year != null) search.set("year", String(filters.year));
    if (filters.pageSize != null) {
      search.set("limit", String(filters.pageSize));
      search.set("offset", String(Math.max(0, ((filters.page ?? 1) - 1) * filters.pageSize)));
    }
    const query = search.toString();
    return call<PayslipRow[]>(query ? `${PAYSLIPS_PATH}?${query}` : PAYSLIPS_PATH);
  },
};

/**
 * A correction to a clocked time, with the name the screens cannot read off
 * `employees` themselves.
 */
export interface TimeAmendmentRow extends TimeAmendment {
  employee_name: string | null;
}

/**
 * Correcting a clocked time — the back-dated fill-in.
 *
 * One `create` for both outcomes, because the person is asking the same thing
 * either way and only the server can count what is left of their allowance:
 * the response says whether the correction is already on the timesheet
 * (`applied`) or waiting for an admin.
 */
export const timeAmendments = {
  /** Corrections: the caller's own, their team's, or everybody's. */
  list(options: { employeeId?: string; status?: TimeAmendmentStatus } = {}) {
    const search = new URLSearchParams();
    if (options.employeeId) search.set("employee_id", options.employeeId);
    if (options.status) search.set("status", options.status);
    const query = search.toString();

    return call<TimeAmendmentRow[]>(
      query ? `${TIME_AMENDMENTS_PATH}?${query}` : TIME_AMENDMENTS_PATH,
    );
  },

  /**
   * How many self-service corrections are left this month.
   *
   * Asked before the dialog opens rather than discovered by being refused:
   * "your third of five" and "this one needs an admin" are different screens,
   * and somebody should know which they are on before they type anything.
   */
  allowance(employeeId?: string) {
    const query = employeeId ? `?employee_id=${encodeURIComponent(employeeId)}` : "";
    return call<{ employee_id: string } & AmendmentAllowance>(
      `${timeAmendmentAllowancePath}${query}`,
    );
  },

  /** "This time was wrong, here is the right one." */
  create(body: {
    employee_id: string;
    entry_started_at: string;
    new_started_at?: string | null;
    new_ended_at?: string | null;
    reason?: string | null;
  }) {
    return call<{
      amendment: TimeAmendment;
      /** False when it was recorded as a request instead of taking effect. */
      applied: boolean;
      allowance: AmendmentAllowance;
    }>(TIME_AMENDMENTS_PATH, { method: "POST", body });
  },

  /** Approving is what applies a pending correction; rejecting changes nothing. */
  decide(amendmentId: string, decision: "approve" | "reject" | "cancel", note?: string) {
    return call<TimeAmendment>(timeAmendmentDecisionPath(amendmentId, decision), {
      method: "POST",
      body: { note: note ?? null },
    });
  },
};

/**
 * What is waiting on the person signed in.
 *
 * Takes nothing: the whole answer is decided by who is asking, which is why it
 * is `/me/notifications` rather than a list with filters. See
 * `packages/shared/src/notifications.ts` for why the feed is derived from the
 * requests themselves rather than read from a table of its own.
 */
export const notifications = {
  list() {
    return call<NotificationFeed>(meNotificationsPath);
  },
};

export const api = {
  notifications,
  countries,
  companies,
  afrFilings,
  afrDocumentKinds,
  clientGroups,
  feeSchedules,
  contacts,
  services,
  serviceCategories,
  serviceBundles,
  bankAccounts,
  remittanceDefaults,
  invoiceSettings,
  bulkActions,
  invoiceNotes,
  workLog,
  virrginTransactions,
  invoices,

  // Payroll, leave and attendance.
  employees,
  leaveTypes,
  leaveRequests,
  wfhRequests,
  leaveAllowances,
  setLeaveAllowances,
  holidays,
  payrollRates,
  officeNetworks,
  attendance,
  timeAmendments,
  payPeriods,
  payslips,
  payrollFilings,
};

export type { Filter, ListQuery, OrderBy, PageMeta };
